import { useEffect, useMemo, useState } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';

import type {
  PlayerPlayPostMessageConnectionPlayApi,
  PlayerPlayPostMessageConnectionViewApi,
  PlayTimerState,
} from '@genially/contracts';
import { PlayerPlayP2pClientEventType } from '@genially/p2p-client';
import type { InteractiveQuestionAnswerPayload } from '@genially/p2p-lib';
import { PlayUserType } from '@genially/p2p-lib';
import type { PenpalAsyncMethodReturns } from '@genially/ui';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';

import { useLogRocketService } from '../../../../../../../bootstrap/application/components/app/Routes/hooks/useLogRocketService';
import { GeniallyView } from '../../../../../../../shared/application/components/GeniallyView/GeniallyView';
import { useFullScreen } from '../../../../../../../shared/application/hooks/useFullScreen';
import { usePlayerClientContext } from '../../PlayerClientProvider/hooks/usePlayerClientContext';
import { usePlayerGameContext } from '../../PlayerGame/PlayerGame.context';
import { getRestorableAnswersForPlayer } from './getRestorableAnswersForPlayer';

const NEAR_TO_BOTTOM_THRESHOLD = 250;

interface PlayerGeniallyViewProps {
  showGeniallyAsFullHeight?: boolean;
}

export const PlayerGeniallyView = observer(
  ({ showGeniallyAsFullHeight }: PlayerGeniallyViewProps) => {
    const { client } = usePlayerClientContext();
    const { trackedEvents } = useLogRocketService();

    const { viewConnection, sidebarSummary, setIsMouseNearBottom } =
      usePlayerGameContext();

    const { toggleFullScreen, isAvailable } = useFullScreen({
      isConnected: viewConnection.isConnected,
      setIsFullScreen: viewConnection.connection?.setIsFullScreen,
    });

    const [currentSlideId, setCurrentSlideId] = useState(client.game.currentSlide);

    const resolvedInteractiveQuestionIds = useMemo(() => {
      const resolvedIds: string[] = [];

      for (const key of client.game.interactiveQuestionAnswers.keys()) {
        if (client.game.interactiveQuestionAnswers.get(key)?.isResolved) {
          resolvedIds.push(key);
        }
      }

      return resolvedIds;
    }, [client.game.interactiveQuestionAnswers]);

    const randomQuestionAssignments = useMemo(
      () => toJS(client.game.randomQuestionAssignments),
      // `randomQuestionAssignments` is mutated in place by mobx-keystone's patch
      // application (same object reference), so depending on it directly never
      // sees a change. `version` is a primitive that increments on every applied
      // patch, so it reliably triggers this memo to recompute.
      [client.game.version],
    );

    const handleInteractiveQuestionAnswered = (
      data: InteractiveQuestionAnswerPayload,
    ) => {
      client.sendInteractiveQuestionAnswer(data);
    };

    const handleScoreableInteractiveQuestionAnswered = async (
      data: InteractiveQuestionAnswerPayload,
      isCorrect: boolean,
    ) => {
      await client.sendScoreableInteractiveQuestionAnswer({ answer: data, isCorrect });
    };

    const methods: PlayerPlayPostMessageConnectionPlayApi = {
      onInteractiveQuestionAnswered: handleInteractiveQuestionAnswered,
      onScoreableInteractiveQuestionAnswered: handleScoreableInteractiveQuestionAnswered,
      getInitialData: () => {
        return {
          isFullScreenAvailable: isAvailable,
          currentSlideId: client.game.currentSlide,
          answers: getRestorableAnswersForPlayer(
            client.game.asJS.interactiveQuestionAnswers,
            client.username,
          ),
          resolvedInteractiveQuestions: resolvedInteractiveQuestionIds,
          randomQuestionAssignments,
          // The host's flags, received when it let this player in.
          isTimerSyncEnabled: client.hasFeatureFlag(
            FEATURE_FLAGS.FEAT_LT4_PLAY_TIMER_SYNC,
          ),
        };
      },
      requestTimerState: itemId => {
        client.requestTimerState(itemId);
      },
      toggleFullScreen,
      getLogRocketTrackedEvents: () => {
        return trackedEvents;
      },
      notifyMousePosition: ({ y, viewportHeight }) => {
        setIsMouseNearBottom(y > viewportHeight - NEAR_TO_BOTTOM_THRESHOLD);
      },
    };

    const handleConnectionChange = (
      conn?: PenpalAsyncMethodReturns<PlayerPlayPostMessageConnectionViewApi>,
    ) => {
      if (conn) {
        viewConnection.setConnection(conn);
      } else {
        viewConnection.setConnection(undefined);
      }
    };

    useEffect(() => {
      if (currentSlideId === client.game.currentSlide) {
        return;
      }

      sidebarSummary.close();
      setCurrentSlideId(client.game.currentSlide);
    }, [client.game.currentSlide, currentSlideId, sidebarSummary]);

    useEffect(() => {
      if (!viewConnection.isConnected) {
        return;
      }

      viewConnection.connection.changeSlide(currentSlideId);
    }, [currentSlideId, viewConnection.connection, viewConnection.isConnected]);

    useEffect(() => {
      if (!viewConnection.isConnected) {
        return;
      }

      viewConnection.connection.setResolvedInteractiveQuestions(
        resolvedInteractiveQuestionIds,
      );
    }, [
      resolvedInteractiveQuestionIds,
      viewConnection.connection,
      viewConnection.isConnected,
    ]);

    useEffect(() => {
      const handleAnswerRejected = ({
        interactiveQuestionId,
      }: {
        interactiveQuestionId: string;
      }) => {
        if (!viewConnection.isConnected) return;

        viewConnection.connection.notifyAnswerRejected(interactiveQuestionId);
      };

      client.on(PlayerPlayP2pClientEventType.AnswerRejected, handleAnswerRejected);

      return () => {
        client.off(PlayerPlayP2pClientEventType.AnswerRejected, handleAnswerRejected);
      };
    }, [client, viewConnection.connection, viewConnection.isConnected]);

    useEffect(() => {
      if (!viewConnection.isConnected) {
        return undefined;
      }

      const { connection } = viewConnection;

      const handleTimerStateReceived = (state: PlayTimerState) => {
        connection.applyTimerState(state);
      };

      client.on(
        PlayerPlayP2pClientEventType.TimerStateReceived,
        handleTimerStateReceived,
      );

      return () => {
        client.off(
          PlayerPlayP2pClientEventType.TimerStateReceived,
          handleTimerStateReceived,
        );
      };
    }, [client, viewConnection.connection, viewConnection.isConnected]);

    useEffect(() => {
      if (!viewConnection.isConnected) {
        return;
      }

      viewConnection.connection.setRandomQuestionAssignments(randomQuestionAssignments);
    }, [
      randomQuestionAssignments,
      viewConnection.connection,
      viewConnection.isConnected,
    ]);

    return (
      <GeniallyView<
        PlayerPlayPostMessageConnectionPlayApi,
        PlayerPlayPostMessageConnectionViewApi
      >
        geniallyId={client.geniallyId}
        type={PlayUserType.Player}
        methods={methods}
        showGeniallyAsFullHeight={showGeniallyAsFullHeight}
        onConnectionCreated={handleConnectionChange}
        onConnectionDestroyed={handleConnectionChange}
      />
    );
  },
);
